/**
 * API Service para Cluster Assignments (atribuição de clusters a resellers)
 *
 * Não há wrapper para `GET /clusters/{id}/assignments` (as revendas de UM
 * cluster) — a rota existe no backend, mas nenhuma tela mostra essa informação:
 * o portal do CP consulta o mapa inverso, clusters por revenda. O wrapper
 * chegou a existir sem consumidor e saiu como código morto (#649); quando a tela
 * aparecer, ele volta em cinco linhas.
 */

import apiClient from './api'

export type SDNZoneInfo = {
  zone_id: string
  name: string
  created: boolean
  is_default: boolean
}

export type ClusterAssignment = {
  id: string
  reseller_id: string
  cluster_id: string
  is_active: boolean
  assigned_at: string
  deactivated_at?: string
  deactivation_reason?: string
  created_at: string
  updated_at: string
  reseller?: {
    id: string
    name: string
    type: string
  }
  cluster?: {
    id: string
    name: string
    availability_zone: string
    status: string
  }
  assigned_by_user?: {
    id: string
    email: string
    full_name?: string
  }
  deactivated_by_user?: {
    id: string
    email: string
    full_name?: string
  }
  sdn_zone?: SDNZoneInfo
}

export type ClusterAssignmentListResponse = {
  items: ClusterAssignment[]
  total: number
  page: number
  page_size: number
}

export type MyCluster = {
  id: string
  name: string
  description?: string
  availability_zone: string
  sdn_enabled: boolean
  status: string
  assignment_id: string
  assigned_at: string
  has_relay: boolean
  relay_status?: string
  relay_is_connected: boolean
  gateway_count: number
}

export type MyClusterListResponse = {
  items: MyCluster[]
  total: number
}

const clusterAssignmentsApi = {
  /**
   * Atribui um cluster a um reseller
   * Cria automaticamente uma zona VXLAN dedicada para o reseller
   */
  async assignCluster(clusterId: string, resellerId: string): Promise<ClusterAssignment> {
    const response = await apiClient.post<ClusterAssignment>(
      `/api/v1/cluster-assignments/clusters/${clusterId}/assign`,
      { reseller_id: resellerId },
    )
    return response.data
  },

  /**
   * Remove a atribuição de UM reseller no cluster (desativa).
   *
   * `resellerId` identifica qual revenda sai: um cluster pode servir várias, e
   * omiti-lo só é aceito quando existe uma única atribuição ativa — caso
   * contrário o backend responde 409 em vez de escolher uma.
   */
  async unassignCluster(
    clusterId: string,
    resellerId?: string,
    reason?: string,
  ): Promise<ClusterAssignment> {
    const response = await apiClient.delete<ClusterAssignment>(
      `/api/v1/cluster-assignments/clusters/${clusterId}/unassign`,
      {
        params: resellerId ? { reseller_id: resellerId } : undefined,
        data: reason ? { reason } : undefined,
      },
    )
    return response.data
  },

  /**
   * Lista todas as atribuições de clusters (Cloud Provider)
   */
  async listAssignments(params?: {
    reseller_id?: string
    cluster_id?: string
    is_active?: boolean
    page?: number
    page_size?: number
  }): Promise<ClusterAssignmentListResponse> {
    const response = await apiClient.get<ClusterAssignmentListResponse>(
      '/api/v1/cluster-assignments/cluster-assignments',
      { params },
    )
    return response.data
  },

  /**
   * Lista atribuições percorrendo TODAS as páginas.
   *
   * Existe porque a contagem de atribuições mudou de ordem de grandeza: sob a
   * regra antiga (um reseller por cluster) o total era limitado pelo número de
   * clusters, e a primeira página de 20 bastava. Com o par `(cluster, reseller)`
   * o total é *clusters × revendas*, e uma lista truncada não é só incompleta na
   * tela — ela faz o seletor de atribuição oferecer um cluster que a revenda já
   * tem, e o usuário leva um 409 no clique.
   *
   * O `page_size` é o máximo aceito pelo backend (100, `le=100`).
   *
   * **Sem teto de páginas, e isso é deliberado.** Uma versão anterior truncava
   * em 20 páginas com um `console.warn` — o que recriava, para CPs grandes,
   * exatamente a regressão que este helper existe para evitar: badges de cluster
   * faltando na listagem e o seletor oferecendo um cluster que a revenda já tem
   * (409 no clique). Um aviso no console não é visto por ninguém; lista
   * incompleta que se apresenta como completa é pior que lenta.
   *
   * A performance é resolvida pelo paralelismo, não pelo corte: o `total` vem já
   * na primeira resposta, então as páginas restantes são buscadas de uma vez em
   * vez de uma a uma.
   *
   * **Buraco conhecido, deixado aberto (#649):** a paginação é por `OFFSET`. O
   * desempate por `id` no `ORDER BY` do backend já eliminou a duplicação por
   * empate, mas uma escrita concorrente *entre* a 1ª página e as demais desloca
   * os offsets e pode pular uma linha — a mesma linha faltando que este helper
   * existe para evitar. Só some com paginação por keyset (cursor), e keyset é
   * sequencial: trocaria uma corrida rara pelo fim do paralelismo acima, que é o
   * que segura o tempo de carga num CP grande. Fica registrado, não corrigido.
   */
  async listAllAssignments(params?: {
    reseller_id?: string
    cluster_id?: string
    is_active?: boolean
  }): Promise<ClusterAssignment[]> {
    const PAGE_SIZE = 100

    const primeira = await clusterAssignmentsApi.listAssignments({
      ...params,
      page: 1,
      page_size: PAGE_SIZE,
    })

    // Cabe numa página só (o caso do modal, que filtra por reseller_id), ou o
    // servidor devolveu menos do que o tamanho da página — nada a buscar.
    if (primeira.items.length >= primeira.total || primeira.items.length < PAGE_SIZE) {
      return primeira.items
    }

    const totalDePaginas = Math.ceil(primeira.total / PAGE_SIZE)
    const restantes = await Promise.all(
      Array.from({ length: totalDePaginas - 1 }, (_, i) =>
        clusterAssignmentsApi.listAssignments({ ...params, page: i + 2, page_size: PAGE_SIZE }),
      ),
    )

    return [...primeira.items, ...restantes.flatMap((r) => r.items)]
  },

  /**
   * Lista clusters atribuídos ao reseller atual
   */
  async listMyClusters(): Promise<MyClusterListResponse> {
    const response = await apiClient.get<MyClusterListResponse>(
      '/api/v1/cluster-assignments/my-clusters',
    )
    return response.data
  },

  /**
   * Obtém detalhes de um cluster atribuído (Reseller)
   */
  async getMyCluster(clusterId: string): Promise<MyCluster> {
    const response = await apiClient.get<MyCluster>(
      `/api/v1/cluster-assignments/my-clusters/${clusterId}`,
    )
    return response.data
  },
}

export default clusterAssignmentsApi
